import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../components/ThemeContext';
import { useGlobalFilters } from '../components/GlobalFilterContext';

// ============================================================
// TYPES
// ============================================================
export interface CountrySummary {
  country: string;
  product_count: number;
  active_recall_count: number;
  shortage_count: number;
  high_confidence_pct: number | null;
}

interface CountryOriginListPageProps {
  apiBase: string;
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  IconSearch: React.ComponentType;
  IconSpinner: React.ComponentType;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  IconGlobe: React.ComponentType;
  IconDatabase: React.ComponentType;
  IconAlert: React.ComponentType;
  IconFlag: React.ComponentType;
  IconShield: React.ComponentType;
}

// ============================================================
// HELPER: useMediaQuery hook
// ============================================================
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function CountryOriginListPage({
  apiBase,
  SectionHeader,
  IconSearch,
  IconSpinner,
  StatCard,
  IconGlobe,
  IconDatabase,
  IconAlert,
  IconFlag,
  IconShield,
}: CountryOriginListPageProps) {
  const { T } = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const { filters: globalFilters } = useGlobalFilters();

  // State
  const [countries, setCountries] = useState<CountrySummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [aggregates, setAggregates] = useState<{
    total_countries: number;
    total_products: number;
    total_products_not_covered: number;
    total_active_recalls: number;
    total_shortages: number;
  }>({ total_countries: 0, total_products: 0, total_products_not_covered: 0, total_active_recalls: 0, total_shortages: 0 });

  // Build global filter query params for country origins
  const globalFilterQS = (() => {
    const p = new URLSearchParams();
    if (globalFilters.cooConfidence.length > 0) p.set('coo_confidence', globalFilters.cooConfidence.join(','));
    if (globalFilters.country.length > 0) p.set('country', globalFilters.country.join(','));
    return p.toString();
  })();

  // Fetch countries from backend
  const fetchCountries = useCallback(async (search: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100', offset: '0' });
      if (search) params.set('search', search);
      // Append global filter params
      if (globalFilters.cooConfidence.length > 0) params.set('coo_confidence', globalFilters.cooConfidence.join(','));
      if (globalFilters.country.length > 0) params.set('country', globalFilters.country.join(','));
      const res = await fetch(`${apiBase}/metasupply/countries?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCountries(data.countries || []);
      setTotal(data.total || 0);
      if (data.aggregates) {
        setAggregates(data.aggregates);
      }
    } catch (err) {
      setCountries([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, globalFilterQS]);

  useEffect(() => { fetchCountries(''); }, [fetchCountries]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      fetchCountries(searchInput);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, fetchCountries]);

  // Countries where confidence is meaningless — show "Unknown" instead of a %
  const isUnknownCountry = (country: string) =>
    country === 'Non-US / Unknown' || country === 'No Country Assigned';

  // Determine status for a country row
  const getStatus = (c: CountrySummary): { label: string; color: string; bg: string } => {
    if (c.active_recall_count > 0 && c.shortage_count > 0) {
      return { label: 'Critical', color: T.danger, bg: T.dangerBg };
    }
    if (c.active_recall_count > 0) {
      return { label: 'Recalls Active', color: T.danger, bg: T.dangerBg };
    }
    if (c.shortage_count > 0) {
      return { label: 'Shortages', color: T.warning, bg: T.warningBg };
    }
    return { label: 'Stable', color: T.success, bg: T.successBg };
  };

  // Table header style
  const thStyle: React.CSSProperties = {
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    color: T.textLight,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    textAlign: "left",
    borderBottom: `2px solid ${T.border}`,
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  // Table cell style
  const tdStyle: React.CSSProperties = {
    padding: "14px 16px",
    fontSize: 14,
    borderBottom: `1px solid ${T.borderLight}`,
  };

  // Navigate to products filtered by country
  const handleCountryClick = (country: string) => {
    if (country === 'No Country Assigned') {
      navigate('/products?coo=__no_country__');
    } else {
      navigate(`/products?coo=${encodeURIComponent(country)}`);
    }
  };

  // Render card view (mobile/tablet)
  const renderCountryCard = (c: CountrySummary) => {
    const status = getStatus(c);
    const hasActiveRecalls = c.active_recall_count > 0;
    const hasShortage = c.shortage_count > 0;

    return (
      <div
        key={c.country}
        onClick={() => handleCountryClick(c.country)}
        style={{
          background: T.white,
          border: `1px solid ${hasActiveRecalls ? T.danger + '60' : T.border}`,
          borderRadius: T.radius,
          padding: 16,
          boxShadow: T.shadow,
          transition: "box-shadow 0.2s",
          cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark, marginBottom: 4 }}>
              {c.country}
            </div>
          </div>
          <div style={{ flexShrink: 0, marginLeft: 12, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: T.navy }}>{c.product_count}</span>
            <span style={{ fontSize: 10, color: T.textLight, fontWeight: 600 }}>Products</span>
          </div>
        </div>
        <div style={{ fontSize: 12, color: T.textMid, marginBottom: 8 }}>
          High Confidence: {isUnknownCountry(c.country) ? (
            <span style={{ fontWeight: 700, color: T.textLight }}>Unknown</span>
          ) : c.high_confidence_pct != null ? (
            <span style={{
              fontWeight: 700,
              color: c.high_confidence_pct > 75 ? T.success : c.high_confidence_pct > 50 ? T.warning : T.danger,
            }}>{c.high_confidence_pct}%</span>
          ) : (
            <span style={{ fontWeight: 700, color: T.textLight }}>N/A</span>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {hasActiveRecalls && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.04em",
              background: T.dangerBg, color: T.danger, border: `1px solid ${T.danger}40`,
            }}>
              {c.active_recall_count} Active Recall{c.active_recall_count !== 1 ? 's' : ''}
            </span>
          )}
          {hasShortage && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              textTransform: "uppercase", letterSpacing: "0.04em",
              background: T.warningBg, color: T.warning, border: `1px solid ${T.warning}40`,
            }}>
              {c.shortage_count} Shortage{c.shortage_count !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: status.bg, color: status.color, border: `1px solid ${status.color}40`,
          }}>
            {status.label}
          </span>
        </div>
      </div>
    );
  };

  // Render table row (desktop)
  const renderTableRow = (c: CountrySummary) => {
    const status = getStatus(c);
    return (
      <tr
        key={c.country}
        style={{ transition: "background 0.15s", cursor: "pointer" }}
        onClick={() => handleCountryClick(c.country)}
        onMouseEnter={e => {
          (e.currentTarget as HTMLTableRowElement).style.background = T.offWhite;
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLTableRowElement).style.background = 'transparent';
        }}
      >
        <td style={{ ...tdStyle, fontWeight: 700, color: T.navy, textDecoration: "underline", textDecorationColor: `${T.navy}40` }}>
          {c.country}
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          <span style={{ fontWeight: 700, color: T.navy, fontSize: 15 }}>
            {c.product_count.toLocaleString()}
          </span>
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          {isUnknownCountry(c.country) ? (
            <span style={{ color: T.textLight, fontSize: 12, fontStyle: 'italic' }}>Unknown</span>
          ) : c.high_confidence_pct != null ? (
            <span style={{
              fontWeight: 700, fontSize: 14,
              color: c.high_confidence_pct > 75 ? T.success : c.high_confidence_pct > 50 ? T.warning : T.danger,
            }}>{c.high_confidence_pct}%</span>
          ) : (
            <span style={{ color: T.textLight, fontSize: 12 }}>N/A</span>
          )}
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          {c.active_recall_count > 0 ? (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
              background: T.dangerBg, color: T.danger,
            }}>
              {c.active_recall_count}
            </span>
          ) : (
            <span style={{ color: T.textLight }}>0</span>
          )}
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          {c.shortage_count > 0 ? (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
              background: T.warningBg, color: T.warning,
            }}>
              {c.shortage_count}
            </span>
          ) : (
            <span style={{ color: T.textLight }}>0</span>
          )}
        </td>
        <td style={{ ...tdStyle, textAlign: "center" }}>
          <span style={{
            display: "inline-flex", alignItems: "center",
            padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: "0.04em",
            background: status.bg,
            color: status.color,
          }}>
            {status.label}
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: "0 0 40px 0" }}>
      <SectionHeader sub={`${total} countr${total !== 1 ? 'ies' : 'y'} of origin`}>
        Country Origins
      </SectionHeader>

      {/* Stat Tiles */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr 1fr" : isTablet ? "repeat(3, 1fr)" : "repeat(5, 1fr)",
        gap: isMobile ? 10 : 14,
        marginBottom: 8,
      }}>
        <StatCard
          title="Total Countries"
          value={aggregates.total_countries.toLocaleString()}
          subtitle="In database"
          color={T.navy}
          icon={<IconGlobe />}
        />
        <StatCard
          title="Products Covered"
          value={aggregates.total_products.toLocaleString()}
          subtitle="Across all countries"
          color={T.navy}
          icon={<IconDatabase />}
        />
        <StatCard
          title="Products Not Covered"
          value={aggregates.total_products_not_covered.toLocaleString()}
          subtitle="Unknown or unverified origin"
          color={T.textMid}
          icon={<IconShield />}
        />
        <StatCard
          title="Active Recalls"
          value={aggregates.total_active_recalls.toLocaleString()}
          subtitle="Require attention"
          color={T.danger}
          icon={<IconAlert />}
        />
        <StatCard
          title="Active Shortages"
          value={aggregates.total_shortages.toLocaleString()}
          subtitle="Current supply issues"
          color={T.warning}
          icon={<IconFlag />}
        />
      </div>

      {/* Search bar */}
      <div style={{ position: "relative" }}>
        <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.textLight, display: "flex" }}>
          <IconSearch />
        </div>
        <input
          type="text"
          placeholder="Search countries..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          style={{
            width: "100%", padding: "10px 14px 10px 40px",
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusSm,
            fontSize: 16, color: T.textDark,
            background: T.white,
            outline: "none", boxSizing: "border-box",
          }}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40, color: T.textLight }}>
          <IconSpinner />
        </div>
      ) : countries.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: T.textLight, fontSize: 16 }}>
          {searchInput ? `No countries found for "${searchInput}"` : "No country of origin data found."}
        </div>
      ) : isMobile ? (
        /* Card view for mobile */
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {countries.map(renderCountryCard)}
        </div>
      ) : (
        /* Table view for desktop/tablet */
        <div style={{
          background: T.white,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          overflow: "hidden",
          boxShadow: T.shadow,
        }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>Country Name</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Products</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>High Confidence %</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Active Recalls</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Shortages</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {countries.map(renderTableRow)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
